(() => {
  App.ui = App.ui || {};

  const name = 'popup';
  const moduleData = App.ui[name] || {};

  App.ui[name] = (() => {
    /**
     * @param {string} content HTML
     * @param {Object} properties used to configure the popup
     * @param {object} properties.alert set popup to type alert
     * @param {object} properties.callbacks defined callbacks
     * @param {string} properties.className class name of the popup
     * @param {object} properties.confirm popup is confirmation dialog
     * @param {boolean} properties.confirm.showCloseButton show the close button for confirm dialogs
     * @param {boolean} properties.enableLargeCloseButton enables the large close button (for large devices)
     * @param {boolean} properties.disableCloseButtonText hide close button text
     * @param {boolean} properties.isCssPositioned disables position calculation
     * @param {boolean} properties.noScroll page will not scroll in background
     * @param {boolean} properties.stopPageScroll page will not scroll in background (legacy)
     * @param {string} properties.title title HTML
     * @param {string} properties.titleClassName title class name
     * @param {boolean} properties.unclosable popup cannot be closed (action must be taken via btns)
     * @param {boolean} properties.closableExternally popup should be closed via external reference by calling remove()
     * @param {string} properties.theme popup color theme
     * @return {{hide: function, show: function, isShown: function, remove: function, getElement: function}}
     * @constructor
     */
    const Popup = function (content, properties) {
      const $html = $('html');
      const $body = $('body');

      let activeElement = null;
      let callbacks = {};
      let currentPopup = null;
      let element = null;
      let firstTimer = true;
      let onResizePopupChange = function () {};
      let shown = false;
      let iosScrollTopPos = null;
      const { objHasProp } = App.global;

      const checkKeyDown = function checkKeyDown(e) {
        if (e.keyCode === 27 && shown && !properties.unclosable) {
          currentPopup.remove();
        }
      };

      const addTitleUnderline = function (event) {
        const scrolled = event.currentTarget && event.currentTarget.scrollTop !== 0;
        element
          .find('.popup-window__content-main-title')
          .toggleClass('popup-window__content-main-title-underlined', scrolled);
      };

      const delegateEvents = function () {
        element.on('click', '.popup-window__background, .popup-window__close', () => {
          if (!properties.unclosable) {
            currentPopup.remove();
          }
        });
        $(document).on('keydown', checkKeyDown);
      };

      // eslint-disable-next-line consistent-return
      const buttonCallback = function (type, btn) {
        if (objHasProp(properties, type)) {
          if (objHasProp(properties[type], btn)
            && objHasProp(properties[type][btn], 'callback')
            && typeof properties[type][btn].callback === 'function') {
            properties[type][btn].callback();
            if (!properties.closableExternally) {
              currentPopup.remove();
            }
          } else {
            currentPopup.remove();
            return btn === 'ok';
          }
        }
      };

      const setFocus = function ($elem) {
        $elem.focus();
      };

      const handleEnter = function (event, type, value) {
        if (event.keyCode === 13 && shown) {
          buttonCallback(type, value);
        }
      };

      const delegateAlertEvents = function () {
        element.on('click', '#okButton', () => {
          buttonCallback('alert', 'ok');
        });
        $(document).on('keydown', '#okButton', (e) => {
          handleEnter(e, 'alert', 'ok');
        });
      };

      const delegateConfirmEvents = function () {
        element.on('click', '#okButton', () => {
          buttonCallback('confirm', 'ok');
        });
        element.on('click', '#cancelButton', () => {
          buttonCallback('confirm', 'cancel');
        });
        $(document).on('keydown', '#okButton', (e) => {
          handleEnter(e, 'confirm', 'ok');
        });
        $(document).on('keydown', '#cancelButton', (e) => {
          handleEnter(e, 'confirm', 'cancel');
        });
      };

      const delegateTitleEvents = function () {
        const isMobile = App.global.isMobile();
        element.find('.popup-window__content-with-scroll').on('scroll touchmove', addTitleUnderline);
        $(window).on('resize', onResizePopupChange);
        if (isMobile) {
          $(window).on('touchmove', onResizePopupChange);
        }
      };

      const undelegateEvents = function () {
        element.off('click', '.popup-window__background, .popup-window__close', currentPopup.remove);
        element.find('.popup-window__content-with-scroll').off('scroll touchmove', addTitleUnderline);
        $(window).off('resize touchmove', onResizePopupChange);
        $(document).off('keydown', checkKeyDown);
      };

      // Use hideScrollIos and showScrollIos functions for fix scroll position
      // of background on IOS devices  when popup opened
      const hideScrollIos = function () {
        iosScrollTopPos = window.pageYOffset;
        $html.css({
          position: 'fixed',
          top: `${-iosScrollTopPos}px`,
        });
      };

      const showScrollIos = function () {
        $html.css({
          position: '',
          top: '',
        });
        window.scrollTo(0, iosScrollTopPos);
      };

      const apply = function () {
        let $close = null;
        let $container = null;
        const provide = {
          className: properties.className || '',
          enableLargeCloseButton: !!properties.enableLargeCloseButton,
          disableCloseButtonText: properties.disableCloseButtonText,
          theme: properties.theme || '',
        };
        element = $(JST['popup-widget-ui'](provide)).appendTo($body);
        $close = element.find('.popup-window__close');
        $container = element.find('.popup-window__content');

        element.empty = function emptyPopupElement() {
          $close.removeAttr('style');
          $container.html('');
          callbacks = {};
        };

        element.calculatePosition = function calculatePopupElementPosition() {
          const calculatePopupPosition = function () {
            return {
              'margin-top': -$container.outerHeight() / 2,
            };
          };
          if (!objHasProp(properties, 'isCssPositioned')) {
            $container.css(calculatePopupPosition());
          }
          return element;
        };

        element.content = function makePopupElementContent() {
          let $mainContent = null;
          let $contentWithScroll = null;
          let titleClassName = '';
          const getScrollContentHeight = function () {
            const maxHeightProperty = $container.css('max-height');
            const titleHeight = $container.find('.popup-window__content-main-title').outerHeight() || 0;
            let percentageMaxHeight = null;
            let height = parseInt(maxHeightProperty, 10) - titleHeight;
            const mobileSize = App.global.isMobile() ? 40 : 0;
            if (/%/.test(maxHeightProperty)) {
              percentageMaxHeight = parseInt(maxHeightProperty, 10) / 100 || 1;
              height = parseInt(window.innerHeight * percentageMaxHeight, 10)
                - titleHeight - mobileSize;
            }
            return height;
          };
          const getScrollContentMaxHeight = function () {
            const $contents = $contentWithScroll.find('>*:not(.hide)');
            let contentMaxHeight = null;
            if (!$contents.length) {
              contentMaxHeight = $contentWithScroll.outerHeight();
            } else {
              contentMaxHeight = [].reduce.call(
                $contents,
                (totalHeight, current) => totalHeight + $(current).outerHeight(),
                0,
              );
            }
            return contentMaxHeight + 5.0; // Add 5 pixel to avoid corner case scroll bars
          };
          const calculateScrollContentHeight = function () {
            return {
              height: getScrollContentHeight(),
              'max-height': getScrollContentMaxHeight(),
            };
          };
          const addCallbackToList = function (addToWhat, cb) {
            callbacks[addToWhat] = callbacks[addToWhat] || [];
            if (typeof callbacks[addToWhat] === 'function') {
              callbacks[addToWhat] = [callbacks[addToWhat], cb];
            } else if ($.isArray(callbacks[addToWhat])) {
              callbacks[addToWhat].push(cb);
            }
          };

          $mainContent = $('<div />', {
            class: 'popup-window__content-main',
          });
          $contentWithScroll = $('<article />', {
            class: 'popup-window__content-with-scroll',
          })
            .html(content)
            .appendTo($mainContent);

          $container.prepend($mainContent);

          if (objHasProp(properties, 'callbacks')) {
            callbacks = properties.callbacks;
          }

          if (objHasProp(properties, 'stopPageScroll')) {
            const stopPageScrollClass = 'stop-scrolling';

            addCallbackToList('show', () => {
              $html.add($body).addClass(stopPageScrollClass);
              if (App.global.isIOS()) {
                hideScrollIos();
              }
            });
            addCallbackToList('hide', () => {
              $html.add($body).removeClass(stopPageScrollClass);
              if (App.global.isIOS()) {
                showScrollIos();
              }
            });
          }

          if (objHasProp(properties, 'title')) {
            onResizePopupChange = function () {
              $contentWithScroll.css(calculateScrollContentHeight());
              element.calculatePosition();
            };

            titleClassName = `popup-window__content-main-title ${properties.titleClassName || ''}`;
            $container
              .find('.popup-window__content-main')
              .prepend($(properties.title).addClass(titleClassName));

            addCallbackToList('show', onResizePopupChange);
            delegateTitleEvents();
          }

          if (objHasProp(properties, 'confirm')) {
            let okBtnClassName = 'btn-cta-primary';
            let cancelBtnClassName = 'btn-cta-secondary';
            let okText = 'OK';
            let cancelText = 'CANCEL';

            if (objHasProp(properties.confirm, 'ok')) {
              if (objHasProp(properties.confirm.ok, 'text')) {
                okText = properties.confirm.ok.text;
              }
              if (objHasProp(properties.confirm.ok, 'className')) {
                okBtnClassName = properties.confirm.ok.className;
              }
            }
            if (objHasProp(properties.confirm, 'cancel')) {
              if (objHasProp(properties.confirm.cancel, 'text')) {
                cancelText = properties.confirm.cancel.text;
              }
              if (objHasProp(properties.confirm.cancel, 'className')) {
                cancelBtnClassName = properties.confirm.cancel.className;
              }
            }

            if (!properties.confirm.showCloseButton) {
              $close.remove();
            }
            element.addClass('confirm-wrapper');
            $container
              .append(`<div class="popup-window__buttons">
                         <button id="cancelButton" class=${cancelBtnClassName}>${cancelText}</button>
                         <button id="okButton" class=${okBtnClassName}>${okText}</button>
                       </div>`);

            delegateConfirmEvents();
          }

          if (objHasProp(properties, 'alert')) {
            const alertBtnClass = 'btn-cta-primary';

            const okText = objHasProp(properties.alert, 'ok')
            && objHasProp(properties.alert.ok, 'text')
              ? properties.alert.ok.text
              : 'OK';

            $container
              .append(`<div class="popup-window__buttons">
                         <button id="okButton" class=${alertBtnClass}>${okText}</button>
                       </div>`);
            delegateAlertEvents();
          }

          if (objHasProp(properties, 'unclosable') || content === '') {
            $close.hide();
          }

          return element;
        };

        element.callback = function doPopupElementCallback(what) {
          if (typeof callbacks[what] === 'function') {
            callbacks[what](element);
          } else if ($.isArray(callbacks[what])) {
            callbacks[what].forEach((cb) => {
              if (typeof cb === 'function') {
                cb(element);
              }
            });
          }
          return element;
        };

        firstTimer = false;
      };

      // eslint-disable-next-line consistent-return
      const show = function () {
        // eslint-disable-next-line no-param-reassign
        properties = properties || {};

        if (shown) {
          return currentPopup;
        }

        if (!content) {
          App.sendErrorToRaygun('popup: trying to show popup with empty content');
          return currentPopup;
        }

        if (properties.alert && !properties.alert.disableErrorLog) {
          App.global.sendErrorLog(content);
        }
        if (firstTimer) {
          apply();
          delegateEvents();
        }

        element
          .content()
          .callback('beforeShow')
          .show(0, () => {
            if (properties.noScroll) {
              App.common.header.preventDocumentScroll();
            }
            shown = true;
            element.callback('show');
          })
          .calculatePosition();

        if (properties.confirm || properties.alert) {
          setFocus($('#okButton'));
        } else {
          activeElement = document.activeElement;
          $(activeElement).blur();
        }

        return currentPopup;
      };

      const hide = function () {
        if (shown) {
          element
            .hide(0, () => {
              if (properties.noScroll) {
                App.common.header.allowDocumentScroll();
              }
            })
            .callback('hide')
            .empty();
          shown = false;
        }
        return currentPopup;
      };

      const remove = function () {
        if (shown) {
          element
            .hide(0, () => {
              if (properties.noScroll) {
                App.common.header.allowDocumentScroll();
              }
            })
            .callback('hide')
            .remove();
          shown = false;
          firstTimer = true;
          undelegateEvents();
        }
        return currentPopup;
      };

      currentPopup = {
        show,
        hide,
        remove,
        getElement() {
          return element;
        },
        isShown() {
          return shown;
        },
      };

      return currentPopup;
    };

    const popupConstructor = function (content, properties = {}) {
      if (typeof properties.type === 'undefined') {
        return new Popup(content, properties).show();
      }
      return App.ui.popup.simple(content, properties);
    };

    popupConstructor.Type = moduleData.Type;
    popupConstructor.simple = moduleData.simple;

    return popupConstructor;
  })();
})();
